import _extends from '@babel/runtime-corejs3/helpers/extends';
import _defineProperty from '@babel/runtime-corejs3/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/objectWithoutProperties';
import React__default from 'react';
import classNames from 'classnames';
import styles from './8bb9413bdd14fb51d5d29cbdf0609528177aa9a6e776e76866c59fac1d57e03b.cfe-internal.js';
import { useNumberStepperContext } from './cab259ea986bae84b53c604411632728b26d185d6bd5f24fb5bdb7d35490dde4.cfe-internal.js';
import '../../utils/2d829def7915df1f35176a057ded5c77071616520fe7b6b8ef66f5eb29ca849f.cfe-internal.js';
import { useFormFieldControl } from '../FormField/f390dafefe74ba07f7da711a77443b7b51d96a6ad1aee47361fd1804064e9ba7.cfe-internal.js';
import concatIds from '../../utils/concatIds/99a67648a55cf7a2ee84ef14be5ebb23af1b4b19b5520388cd5eef8f3f3a1e28.cfe-internal.js';
import getSubDataTestId from '../../utils/getSubDataTestId/d234b5cba8708c34be51c9bf4976cdee0c55fe33ce1598dbff38a5cf644e53a8.cfe-internal.js';

var _excluded = ["data-testid", "id", "className", "aria-label", "aria-labelledby", "aria-describedby", "onKeyDown", "onFocus", "onBlur"];
/**
 * Renders the NumberStepperInput component.
 * Either `NumberStepper` renders this component automatically or the consumer renders this component explicitly.
 */
var NumberStepperInput = /*#__PURE__*/React__default.forwardRef(function (_ref, ref) {
  var dataTestIdFromProps = _ref['data-testid'],
    idFromProps = _ref.id,
    className = _ref.className,
    ariaLabelFromProps = _ref['aria-label'],
    ariaLabelledByFromProps = _ref['aria-labelledby'],
    ariaDescribedByFromProps = _ref['aria-describedby'],
    onKeyDown = _ref.onKeyDown,
    onFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    rest = _objectWithoutProperties(_ref, _excluded);
  // Context from NumberStepper
  var _useNumberStepperCont = useNumberStepperContext(),
    isExternallyDisabled = _useNumberStepperCont.isExternallyDisabled,
    dataTestId = _useNumberStepperCont.dataTestId,
    inputId = _useNumberStepperCont.inputId,
    inputValue = _useNumberStepperCont.inputValue,
    increment = _useNumberStepperCont.increment,
    decrement = _useNumberStepperCont.decrement,
    increaseToMax = _useNumberStepperCont.increaseToMax,
    decreaseToMin = _useNumberStepperCont.decreaseToMin,
    inputStep = _useNumberStepperCont.step,
    inputMin = _useNumberStepperCont.min,
    inputMax = _useNumberStepperCont.max,
    ariaLabel = _useNumberStepperCont.ariaLabel,
    ariaLabelledBy = _useNumberStepperCont.ariaLabelledBy,
    ariaDescribedBy = _useNumberStepperCont.ariaDescribedBy,
    handleInputChange = _useNumberStepperCont.handleInputChange,
    handleInputBlur = _useNumberStepperCont.handleInputBlur;

  // TODO: Add proper styling for the form field label (this is a breaking change)
  var _useFormFieldControl = useFormFieldControl(),
    formFieldHelperTextId = _useFormFieldControl.helperTextId,
    formFieldErrorTextId = _useFormFieldControl.errorTextId;
  var internalAriaDescribedby = concatIds(ariaDescribedByFromProps !== null && ariaDescribedByFromProps !== void 0 ? ariaDescribedByFromProps : ariaDescribedBy, formFieldHelperTextId, formFieldErrorTextId);

  // Indicates if the `inputValue` is invalid
  var isInputValueInvalid = typeof inputValue === 'number' && (inputValue > inputMax || inputValue < inputMin);

  /**
   * Calls `onKeyDown` if it is defined.
   * Calls `increment`, `decrement`, `increaseToMax`, or `decreaseToMin`
   * depending on the key that was pressed.
   */
  var handleKeyDown = function handleKeyDown(e) {
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(e);
    if (isExternallyDisabled) return;
    if (e.key === 'ArrowUp') {
      // Prevents browser's default increment
      e.preventDefault();
      increment();
    } else if (e.key === 'ArrowDown') {
      // Prevents browser's default decrement
      e.preventDefault();
      decrement();
    } else if (e.key === 'End') {
      // Prevents browser's default page scroll
      e.preventDefault();
      increaseToMax();
    } else if (e.key === 'Home') {
      // Prevents browser's default page scroll
      e.preventDefault();
      decreaseToMin();
    }
    // The w3 defines 'PageUp' and 'PageDown' as optional behavior.
    // We decided not to add this optional behavior, but still wanted to prevent default scrolling behavior.
    else if (e.key === 'PageUp') {
      // Prevents browser's default page scroll
      e.preventDefault();
    } else if (e.key === 'PageDown') {
      // Prevents browser's default page scroll
      e.preventDefault();
    }
  };

  /**
   * Calls `onFocus` if it is defined.
   * Selects the DOM element
   */
  var handleFocus = function handleFocus(e) {
    onFocus === null || onFocus === void 0 || onFocus(e);
    if (isExternallyDisabled) return;
    e.target.select();
  };

  /**
   * Calls `onBlur` if it is defined.
   * Calls `handleInputBlur`
   */
  var handleBlur = function handleBlur(e) {
    onBlur === null || onBlur === void 0 || onBlur(e);
    if (isExternallyDisabled) return;
    handleInputBlur(e);
  };
  return /*#__PURE__*/React__default.createElement("input", _extends({}, rest, {
    // `type`, `step`, `min`, `max`, `onChange`, and `value` are purposely overwritten
    ref: ref,
    "data-testid": dataTestIdFromProps !== null && dataTestIdFromProps !== void 0 ? dataTestIdFromProps : getSubDataTestId(dataTestId, 'Input'),
    id: idFromProps !== null && idFromProps !== void 0 ? idFromProps : inputId,
    className: classNames(className, styles.numberStepperInput, _defineProperty(_defineProperty({}, styles.invalid, isInputValueInvalid), styles.disabled, isExternallyDisabled)),
    type: "number",
    step: inputStep,
    min: inputMin,
    max: inputMax,
    value: inputValue,
    "aria-valuemin": inputMin,
    "aria-valuemax": inputMax,
    "aria-valuenow": Number(inputValue),
    "aria-label": ariaLabelFromProps !== null && ariaLabelFromProps !== void 0 ? ariaLabelFromProps : ariaLabel,
    "aria-labelledby": ariaLabelledByFromProps !== null && ariaLabelledByFromProps !== void 0 ? ariaLabelledByFromProps : ariaLabelledBy,
    "aria-describedby": internalAriaDescribedby,
    "aria-invalid": isInputValueInvalid,
    "aria-disabled": isExternallyDisabled,
    onChange: isExternallyDisabled ? undefined : handleInputChange,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur
  }));
});

export { NumberStepperInput as default };
